import React, { useState } from 'react';
import Swiper from 'swiper/types/swiper-class';
import { debounce } from 'lodash';
import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import { Box, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { CapaQuotationPartnerStrategyFile } from '@generated/graphql';
import { clsx, colorSet } from '@components/Provider';
import CustomizedSwiper from '@components/Customized/CustomizedSwiper';
import DetailViewDialog from '../DetailViewDialog';
import { MediaSwiperMobile } from './MediaSwiperMobile';
import useMediaSwiper from './useMediaSwiper';
export interface MediaSwiperProps {
  files: CapaQuotationPartnerStrategyFile[];
  backgroundColor: 'black' | 'white';
  initialSlide?: number;
  width?: string | number;
}

const MediaSwiper = ({
  files,
  backgroundColor,
  initialSlide = 0,
  width
}: MediaSwiperProps) => {
  const {
    siwper,
    paginationFraction,
    navigationButton,
    swiperSlide
  } = useStyles();
  const [isMouseHoverCarousel, setIsMouseHoverCarousel] = useState(false);
  const [isOpenDetailView, setIsOpenDetailView] = useState(-1);
  const {
    useSwipeSlides,
    handleIframePointerEvent,
    handleStopVideo
  } = useMediaSwiper();
  const SwipeSlides = useSwipeSlides(files, clsx(swiperSlide));
  const matches = useMediaQuery(useTheme().breakpoints.down(900));

  const isOne = files.length === 1;
  const isDisplayFraction = isOne || backgroundColor === 'white';

  const matchesWin = window as Window & { matches?: boolean };
  matchesWin.matches = matches;

  const handleMouseEnter = () => {
    if (matches) {
      return;
    }
    handleMouseLeave.cancel();
    setIsMouseHoverCarousel(true);
  };

  const handleMouseLeave = debounce(() => {
    if (matches) {
      return;
    }
    setIsMouseHoverCarousel(false);
  }, 3000);

  const handleClickSwiper = (swiper: Swiper, e: any) => {
    if (!matchesWin.matches) {
      return;
    }
    e.stopPropagation();

    const getIndex = swiper.clickedIndex % files.length;
    setIsOpenDetailView((getIndex ? getIndex : files.length) - 1);
  };

  return (
    <Box
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      width={'100%'}
      height={matches ? 224 : 654}
      bgcolor={backgroundColor}
      position={'relative'}
      overflow={'hidden'}
    >
      {!isDisplayFraction && (
        <Box className={clsx('pagination-fraction', paginationFraction)} />
      )}
      <Box
        maxWidth="100%"
        maxHeight="100%"
        height="100%"
        overflow={'hidden'}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {!isOne && (
          <>
            <Box
              className={clsx('next-btn', navigationButton, {
                'isMouseHover': isMouseHoverCarousel
              })}
            >
              <ChevronRight />
            </Box>
            <Box
              className={clsx('prev-btn', navigationButton, {
                'isMouseHover': isMouseHoverCarousel
              })}
            >
              <ChevronLeft />
            </Box>
          </>
        )}

        <CustomizedSwiper
          onClick={handleClickSwiper}
          className={siwper}
          slidesPerView={1}
          initialSlide={initialSlide}
          pagination={{ type: 'fraction', el: '.pagination-fraction' }}
          navigation={{
            nextEl: '.next-btn',
            prevEl: '.prev-btn'
          }}
          loop={!isOne}
          onSliderFirstMove={(swiper) => {
            handleStopVideo(swiper.slides[swiper.activeIndex]);
          }}
          onTransitionEnd={(swiper) => {
            handleIframePointerEvent(swiper.slides[swiper.activeIndex], 'auto');
          }}
          onSlideChange={(swiper) =>
            handleStopVideo(swiper.slides[swiper.previousIndex])
          }
        >
          {SwipeSlides}
        </CustomizedSwiper>
      </Box>
      <DetailViewDialog
        isOpen={isOpenDetailView >= 0 && matches}
        onClose={() => setIsOpenDetailView(-1)}
        closeIcon={<ChevronLeft size={24} color={colorSet.gray900} />}
      >
        <MediaSwiperMobile
          initialSlide={isOpenDetailView}
          files={files}
          isOne={isOne}
        />
      </DetailViewDialog>
    </Box>
  );
};

export default MediaSwiper;

const useStyles = makeStyles((theme) => ({
  siwper: {
    display: 'flex',
    width: '100%',
    height: '100%',
    '& .swiper-wrapper': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  paginationFraction: {
    position: 'absolute',
    boxShadow: `0 0 0 1px ${colorSet.gray400} inset`,
    backgroundColor: colorSet.gray000 + 'E5',
    opacity: 0.8,
    color: colorSet.gray900,
    width: 'fit-content',
    height: 'fit-content',
    borderRadius: 8,
    alignItems: 'center',
    padding: '6px 12px 8px',
    fontSize: 13,
    top: 16,
    right: 16,
    left: 'unset',
    zIndex: 10,
    [theme.breakpoints.down('md')]: {
      top: 'unset',
      bottom: 16
    }
  },
  navigationButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: colorSet.gray000,
    position: 'absolute',
    zIndex: 30,
    cursor: 'pointer',
    top: `calc(50% - 36px/2)`,
    boxShadow: theme.shadows[1],
    transition: 'all 0.2s ease-in-out',
    '& svg': {
      width: 24,
      heigth: 24
    },
    '&.next-btn': {
      right: 16
    },
    '&.prev-btn': {
      left: 16
    },

    opacity: 0,
    '&.isMouseHover': {
      opacity: 1
    },
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  swiperSlide: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& img,video': {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    },
    '& iframe': {
      margin: `0 73px`
    }
  }
}));
